import React, { useState, useEffect, useMemo } from "react";
import {
  Modal, ModalHeader, ModalBody,
  Nav, NavItem, NavLink, Badge, Input, Spinner,
} from "reactstrap";
import DataTable from "react-data-table-component";
import * as XLSX from "xlsx";
import { BACKEND } from "../../../../consts/backend";
import { ALERT_META, GROUP_CONFIG, filterDespachosByType } from "./requests";

// ─── Local styles (injected once for portability) ─────────────────────────────

{
  let _se = document.getElementById("alertas-modal-styles");
  if (!_se) {
    _se = document.createElement("style");
    _se.id = "alertas-modal-styles";
    document.head.appendChild(_se);
  }
  _se.textContent = `
    /* Group modal */
    .alertas-group-modal .modal-content {
      border: none; border-radius: 14px;
      box-shadow: 0 15px 35px rgba(50,50,93,.1), 0 5px 15px rgba(0,0,0,.07);
      max-height: 90vh; display: flex; flex-direction: column; overflow: hidden;
    }
    .alertas-group-modal .modal-header {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white; border-bottom: none;
      border-radius: 14px 14px 0 0; padding: 16px 24px; flex-shrink: 0;
    }
    .alertas-group-modal .modal-header .modal-title,
    .alertas-group-modal .modal-header .modal-title em { color: white !important; }
    .alertas-group-modal .modal-header .btn-close { filter: invert(1); }
    .alertas-group-modal .modal-body { overflow-y: auto; padding: 20px; flex: 1; }

    /* Modal nav pills */
    .alertas-group-modal .nav-pills {
      flex-wrap: wrap; gap: 8px; padding-bottom: 16px;
      border-bottom: 1px solid #e5e9f2; margin-bottom: 0;
    }
    .alertas-group-modal .nav-pills .nav-link {
      border-radius: 8px; font-size: .82rem; font-weight: 500;
      padding: 8px 16px; color: #526484; background: #f5f6fa;
      border: 1px solid #e5e9f2; transition: all .2s; cursor: pointer;
      display: inline-flex; align-items: center; gap: 6px; white-space: nowrap;
    }
    .alertas-group-modal .nav-pills .nav-link:hover { background: rgba(102,126,234,.08); color: #5e72e4; border-color: #d0d6f5; }
    .alertas-group-modal .nav-pills .nav-link.active {
      background: linear-gradient(135deg, #a78bfa 0%, #8b5cf6 100%);
      color: white !important; border-color: transparent;
      box-shadow: 0 4px 12px rgba(139,92,246,.35);
    }
    .alertas-group-modal .nav-pills .nav-link.active em {
      color: white !important;
    }

    /* DataTable toolbar */
    .dt-toolbar {
      display: flex; align-items: center; gap: 8px;
      padding: 8px 0; flex-wrap: nowrap;
    }
    .dt-btn-excel {
      flex-shrink: 0;
      background: linear-gradient(135deg, #2dce89 0%, #20ab6e 100%);
      border: none; border-radius: 8px; padding: 6px 14px;
      color: white; font-size: .82rem; font-weight: 600;
      display: inline-flex; align-items: center; gap: 5px;
      transition: all .2s; cursor: pointer; white-space: nowrap;
    }
    .dt-btn-excel:hover {
      background: linear-gradient(135deg, #20ab6e 0%, #1a9560 100%);
      transform: translateY(-1px); color: white;
    }
    .dt-rows-info { flex-shrink: 0; font-size: .75rem; color: #8094ae; font-weight: 500; white-space: nowrap; }
  `;
}

// ─── Local helpers ────────────────────────────────────────────────────────────

const DATE_COLS = new Set(["ata", "eta", "etd", "atd", "created_at", "oficializacion", "release_date", "fecha_oficializacion", "fecha_creacion_cargoes", "ultima_fecha_actualizacion_cargoes"]);

const formatDate = (val) => {
  if (val === null || val === undefined || val === "") return "\u2014";
  const s = String(val);
  // Caso ISO: empieza con YYYY-MM-DD → parsear directo sin timezone shift
  const isoMatch = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    const [, y, m, d] = isoMatch;
    return `${d}/${m}/${y}`;
  }
  // Caso datetime completo (ej: "Wed, 13 May 2026 12:48:14 GMT") → usar UTC
  const dt = new Date(s);
  if (isNaN(dt.getTime())) return s;
  const d = String(dt.getUTCDate()).padStart(2, "0");
  const m = String(dt.getUTCMonth() + 1).padStart(2, "0");
  const y = dt.getUTCFullYear();
  return `${d}/${m}/${y}`;
};

const getPoUrl = (po, group, row) => {
  if (!po) return null;
  if (group === "EXPO") return `${process.env.PUBLIC_URL}/expo-detailOrder/${po}`;
  if (group === "IMPO") return `${process.env.PUBLIC_URL}/detailorder/${po}`;
  const tipo = (row.tipo_orden || row.type || "").toString().toUpperCase();
  if (tipo === "E" || tipo === "EXPO") return `${process.env.PUBLIC_URL}/expo-detailOrder/${po}`;
  if (tipo === "I" || tipo === "IMPO") return `${process.env.PUBLIC_URL}/detailorder/${po}`;
  return null;
};

const PoLink = ({ po, url }) => {
  if (!url) return <span>{po ?? "\u2014"}</span>;
  return (
    <a href={url} target="_blank" rel="noopener noreferrer"
      title="Click para ver DetailOrder"
      style={{ color: "#1565c0", textDecoration: "underline", fontWeight: 500 }}>
      {po}
    </a>
  );
};

const DESPACHO_COLS = new Set(["despacho"]);

const DespachoLink = ({ despacho }) => {
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);

  if (!despacho || despacho === "\u2014") return <span>{despacho ?? "\u2014"}</span>;

  const handleClick = async (e) => {
    e.preventDefault();
    if (loading) return;
    setNotFound(false);
    setLoading(true);
    try {
      const base = (BACKEND || "").replace(/\/$/, "");
      const res = await fetch(`${base}/order/${encodeURIComponent(despacho)}/files_dispatch`);
      const data = await res.json();
      const url = data?.files?.[0]?.file;
      if (url) {
        window.open(url, "_blank", "noopener,noreferrer");
      } else {
        setNotFound(true);
      }
    } catch {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <span>
      <a
        href="#"
        onClick={handleClick}
        title="Click para ver Doc"
        style={{ color: "#0369a1", textDecoration: "underline", fontWeight: 500 }}
      >
        {loading
          ? <Spinner size="sm" style={{ width: 12, height: 12, borderWidth: 2 }} />
          : despacho}
      </a>
      {notFound && <span style={{ fontSize: ".7rem", color: "#e85347", marginLeft: 4 }}>Sin archivo</span>}
    </span>
  );
};

const downloadExcel = (rows, filename) => {
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Alertas");
  XLSX.writeFile(wb, `${filename}.xlsx`);
};

// ─── AlertDataTable ───────────────────────────────────────────────────────────

export const AlertDataTable = ({ alertKey, rows, filterType }) => {
  const [search, setSearch] = useState("");
  const meta = ALERT_META[alertKey];
  // Only filter by tipo_orden for DESPACHOS-group alerts; type-specific alerts don't have that column
  const baseRows = (filterType && meta?.group === "DESPACHOS")
    ? filterDespachosByType(rows, filterType)
    : (Array.isArray(rows) ? rows : []);

  // Auto-detect type from data when filterType is null (e.g. GroupModal) and filterCols exist
  const effectiveFilterType = filterType || (meta?.filterCols ? (() => {
    const rawRows = Array.isArray(rows) ? rows : [];
    if (!rawRows.length) return null;
    const types = Object.keys(meta.filterCols);
    for (const t of types) {
      const filtered = filterDespachosByType(rawRows, t);
      if (filtered.length === rawRows.length) return t;
    }
    return null;
  })() : null);

  const cols = (effectiveFilterType && meta?.filterCols?.[effectiveFilterType]) || meta?.cols || Object.keys(baseRows[0] || {});

  // Sort default: created_at desc, fallback to first date col desc
  const DATE_COL_PRIORITY = ["created_at", "ata", "eta", "etd", "atd", "oficializacion", "release_date"];
  const defaultSortCol = DATE_COL_PRIORITY.find(c => cols.includes(c)) || null;

  // Min-width based on both header label length AND max content length in actual data
  const colWidths = useMemo(() => {
    const CHAR_PX = 7.5;
    return Object.fromEntries(cols.map(col => {
      const label = meta?.colLabels?.[col] || col.replace(/_/g, " ").toUpperCase();
      const headerW = Math.round(label.length * CHAR_PX) + 32;
      const maxContentLen = baseRows.reduce((max, row) => {
        const str = DATE_COLS.has(col) ? "DD/MM/AAAA" : String(row[col] ?? "—");
        return Math.max(max, str.length);
      }, 0);
      const contentW = Math.round(maxContentLen * CHAR_PX) + 24;
      return [col, `${Math.max(80, headerW, contentW)}px`];
    }));
  }, [baseRows]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pre-sort by date column descending (most recent first)
  const sortedBaseRows = useMemo(() => {
    const sortCol = ["created_at", "ata", "eta", "etd", "atd", "oficializacion", "release_date"].find(c => cols.includes(c));
    if (!sortCol) return baseRows;
    return [...baseRows].sort((a, b) => {
      const ta = a[sortCol] ? new Date(a[sortCol]).getTime() : 0;
      const tb = b[sortCol] ? new Date(b[sortCol]).getTime() : 0;
      return tb - ta;
    });
  }, [baseRows]); // eslint-disable-line react-hooks/exhaustive-deps

  const columns = cols.map((col) => {
    const label = meta?.colLabels?.[col] || col.replace(/_/g, " ").toUpperCase();
    return {
      id: col,
      name: label,
      selector: (row) => row[col],
      sortable: true,
      wrap: true,
      minWidth: (col === "po" || col === "purchase_order")
        ? "120px"
        : (col === "segments_faltantes" ? "95px" : colWidths[col]),
      sortFunction: DATE_COLS.has(col)
        ? (a, b) => {
            const da = new Date(a[col] ?? 0);
            const db = new Date(b[col] ?? 0);
            return (isNaN(da) ? 0 : da) - (isNaN(db) ? 0 : db);
          }
        : undefined,
      cell: (row) => {
        const val = row[col];
        if (col === "tipo_orden" || col === "type") {
          if (!val) return <span>{"\u2014"}</span>;
          const isImpo = val === "I" || val === "IMPO";
          return <Badge style={isImpo
            ? { background: "#1565c0", color: "white" }
            : { background: "#1e9c5a", color: "white" }
          }>{isImpo ? "IMPO" : "EXPO"}</Badge>;
        }
        if (col === "existe_en_despachos" || col === "existe_en_simis" || col === "existe_en_s3")
          return Number(val) === 1 ? <Badge color="success">S{"\u00ed"}</Badge> : <Badge color="danger">No</Badge>;
        if (col === "segments_faltantes")
          return Number(val) === 1 ? <Badge color="danger">S{"\u00ed"}</Badge> : <Badge color="success">No</Badge>;
        if (col === "estado_cargoes") {
          if (!val) return <span>{"\u2014"}</span>;
          const uVal = String(val).toUpperCase();
          let color = "secondary";
          if (uVal === "UNTRACKABLE") color = "danger";
          else if (uVal === "ACTIVE") color = "warning";
          return <Badge color={color}>{val}</Badge>;
        }
        if ((col === "po" || col === "purchase_order") && meta?.group !== "MRO") {
          const url = getPoUrl(val, filterType || meta?.group, row);
          return <PoLink po={val} url={url} />;
        }
        if (col === "pos") {
          if (!val) return <span>{"\u2014"}</span>;
          const items = String(val).split(/[,;]/).map(s => s.trim()).filter(Boolean);
          return (
            <span style={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
              {items.map((po, i) => {
                const url = getPoUrl(po, filterType || meta?.group, row);
                return <span key={i}>{i > 0 && <span style={{ color: "#8094ae", marginRight: 3 }}>,</span>}<PoLink po={po} url={url} /></span>;
              })}
            </span>
          );
        }
        if (col === "despachos") {
          if (!val) return <span>{"\u2014"}</span>;
          const items = String(val).split(/[,;]/).map(s => s.trim()).filter(Boolean);
          return (
            <span style={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
              {items.map((d, i) => (
                <span key={i}>{i > 0 && <span style={{ color: "#8094ae", marginRight: 3 }}>,</span>}<DespachoLink despacho={d} /></span>
              ))}
            </span>
          );
        }
        if (DESPACHO_COLS.has(col)) return <DespachoLink despacho={val} />;
        if (DATE_COLS.has(col)) return <span>{formatDate(val)}</span>;
        return <span>{val ?? "\u2014"}</span>;
      },
    };
  });

  const filteredRows = useMemo(() => {
    if (!search.trim()) return sortedBaseRows;
    const term = search.toLowerCase();
    return sortedBaseRows.filter((r) =>
      Object.values(r).some((v) => String(v ?? "").toLowerCase().includes(term))
    );
  }, [sortedBaseRows, search]);

  if (!Array.isArray(baseRows) || baseRows.length === 0) return null;

  const handleExcel = () => {
    const now = new Date();
    const dd = String(now.getDate()).padStart(2, "0");
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const yyyy = now.getFullYear();
    const hh = String(now.getHours()).padStart(2, "0");
    const min = String(now.getMinutes()).padStart(2, "0");
    const timestamp = `${dd}-${mm}-${yyyy} ${hh}${min}`;
    const typePart = filterType ? ` (${filterType})` : "";
    const filename = `${meta?.label || alertKey}${typePart} - ${timestamp}`;

    const exportData = filteredRows.map((r) => {
      const obj = {};
      cols.forEach((c) => {
        const val = r[c] ?? "";
        obj[c.replace(/_/g, " ").toUpperCase()] = DATE_COLS.has(c) ? formatDate(val) : val;
      });
      return obj;
    });
    downloadExcel(exportData, filename);
  };

  return (
    <>
      <div className="dt-toolbar">
        <Input
          bsSize="sm"
          placeholder="Buscar..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ flex: 1, minWidth: 0 }}
        />
        <button className="dt-btn-excel" onClick={handleExcel}>
          <em className="icon ni ni-download-cloud" />
          Excel
        </button>
        {search && (
          <span className="dt-rows-info">{filteredRows.length}/{baseRows.length}</span>
        )}
      </div>
      <DataTable
        columns={columns}
        data={filteredRows}
        striped
        highlightOnHover
        pagination
        paginationPerPage={10}
        paginationRowsPerPageOptions={[10, 25, 50]}
        dense
        noDataComponent="Sin registros"
        defaultSortFieldId={defaultSortCol || undefined}
        defaultSortAsc={false}
        customStyles={{
          headCells: { style: { fontSize: "0.72rem", fontWeight: 700, color: "#526484", textTransform: "uppercase", backgroundColor: "#f5f6fa", whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.3 } },
          cells: { style: { fontSize: "0.83rem" } },
        }}
      />
    </>
  );
};

// ─── GroupModal ───────────────────────────────────────────────────────────────

export const GroupModal = ({ isOpen, toggle, group, alerts }) => {
  const [activeKey, setActiveKey] = useState(null);

  const cfg = GROUP_CONFIG[group] || {};
  const keys = group
    ? Object.entries(ALERT_META).filter(([, m]) => m.group === group).map(([k]) => k)
    : [];
  const available = keys.filter((k) => Array.isArray(alerts?.[k]) && alerts[k].length > 0);

  useEffect(() => {
    setActiveKey(available[0] || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group, isOpen]);

  if (!group) return null;

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl" className="alertas-group-modal" scrollable>
      <ModalHeader toggle={toggle} style={{ color: "white" }}>
        <em className={`icon ni ni-${cfg.icon || "alert"} me-2`} style={{ marginRight: "5px" }} />
        Alertas {"\u2014"} {cfg.label || group}
      </ModalHeader>
      <ModalBody>
        {available.length === 0 ? (
          <p className="text-soft text-center py-4">Sin alertas activas para este grupo.</p>
        ) : (
          <>
            <Nav pills>
              {available.map((k) => {
                const meta = ALERT_META[k];
                const cnt = alerts[k].length;
                const sevColor = { danger: "#e85347", warning: "#f4bd0e", info: "#09c2de" }[meta?.severity] || "#8094ae";
                return (
                  <NavItem key={k}>
                    <NavLink
                      active={activeKey === k}
                      onClick={() => setActiveKey(k)}
                      style={{ cursor: "pointer" }}
                      title={meta?.tooltip || meta?.label}
                    >
                      <em className={`icon ni ni-${meta?.icon || "alert"}`}
                        style={{ color: activeKey === k ? "white" : sevColor, fontSize: ".9rem", marginRight: 4 }} />
                      {meta?.label}
                      <span className="badge-cnt" style={{
                        marginLeft: 6,
                        background: sevColor,
                        color: "white",
                        borderRadius: 20, fontSize: ".65rem",
                        padding: "2px 7px", fontWeight: 700,
                      }}>{cnt}</span>
                    </NavLink>
                  </NavItem>
                );
              })}
            </Nav>
            {activeKey && (
              <div style={{ paddingTop: 16 }}>
                <p className="text-soft mb-1" style={{ fontSize: ".78rem", fontWeight: 600 }}>
                  {ALERT_META[activeKey]?.label}
                  {ALERT_META[activeKey]?.tooltip && (
                    <span className="text-muted ms-2 fw-normal" style={{ fontSize: ".72rem" }}>
                      — {ALERT_META[activeKey]?.tooltip}
                    </span>
                  )}
                </p>
                <AlertDataTable alertKey={activeKey} rows={alerts[activeKey]} filterType={null} />
              </div>
            )}
          </>
        )}
      </ModalBody>
    </Modal>
  );
};

export default GroupModal;
